import { i18n } from '@/i18n';
import {
  checkActivationPublished,
  checkActivationUnPublished,
  getRoutePathWithId,
  isActivationLIB,
  setWhiteLabelForLIB,
} from '@/utils/router-utils';

import { AuthGuard } from '@/auth/auth-guards';
import { aboutMenu } from '@/views/about/AboutMenu';
import { collectorRoutePaths } from '@/views/collector-route-paths';
import type { RouteRecordRaw } from 'vue-router';
import { checkViewingRoomExists } from '@/guards/viewingRoom.guard';
import { getWhiteLabelState } from '@/stores/storeFacade';

const { t } = i18n.global;

export const getCollectorRoute = (path: string, id?: string) => {
  if (id) {
    return getRoutePathWithId(path, id);
  }
  return path;
};

export const checkoutRouteName = 'Checkout';
export const offerEditRouteName = 'EditOffer';
export const offerCreateRouteName = 'CreateOffer';
export const listingsRouteName = 'Listings';
export const listingRouteName = 'Listing';
export const viewingRoomRouteName = 'ViewingRoom';
export const offersRouteName = 'Offers';
export const salesAgreementsRouteName = 'SalesAgreement';
export const sellerDetailRouteName = 'SellerDetail';
export const sellerListingsRouteName = 'SellerListings';
export const sellerProfileRouteName = 'SellerProfile';

export const inquiryRoutes = [
  {
    name: offersRouteName,
    meta: { title: t('storefront.app.offers.title') },
    path: collectorRoutePaths.OFFERS,
    component: () => import('@/views/offers/Offers.vue'),
    beforeEnter: [AuthGuard, checkActivationUnPublished],
  },
  {
    name: salesAgreementsRouteName,
    meta: { title: t('storefront.app.salesAgreements.page.title') },
    path: collectorRoutePaths.SALES_AGREEMENTS,
    component: () => import('@/views/sales-agreements/SalesAgreements.vue'),
    beforeEnter: [AuthGuard, checkActivationUnPublished],
  },
];

export const getListingRoutes = () => {
  return [
    {
      meta: { title: t('storefront.app.listings.title') },
      path: collectorRoutePaths.LISTINGS,
      name: listingsRouteName,
      component: () => import('@/views/artworks/Listings.vue'),
      beforeEnter: [checkActivationUnPublished],
    },
    {
      path: collectorRoutePaths.HOME,
      redirect: collectorRoutePaths.LISTINGS,
    },
  ];
};

export const getSellerRoutes = () => {
  return [
    {
      path: collectorRoutePaths.SELLERS,
      name: 'Sellers',
      component: () => import('@/views/sellers/Sellers.vue'),
      beforeEnter: [checkActivationUnPublished],
      meta: {
        title: t('storefront.app.sellers.title'),
      },
    },
    {
      path: collectorRoutePaths.SELLER_DETAIL,
      name: sellerDetailRouteName,
      component: () => import('@/views/sellers/seller-detail/SellerDetail.vue'),
      beforeEnter: [checkActivationUnPublished],
      children: [
        {
          path: '',
          name: sellerListingsRouteName,
          component: () => import('@/views/sellers/seller-listings/SellerListings.vue'),
        },
        {
          path: 'profile',
          name: sellerProfileRouteName,
          component: () => import('@/views/sellers/seller-profile/SellerProfile.vue'),
        },
      ],
    },
  ];
};

export const getCollectorRoutes = (): Array<RouteRecordRaw> => {
  return [
    {
      path: collectorRoutePaths.ROOT,
      component: () => import('@/views/Home.vue'),
      children: [
        {
          path: '',
          redirect: collectorRoutePaths.LISTINGS,
          beforeEnter: [checkActivationUnPublished],
        },
        ...getSellerRoutes(),
        ...getListingRoutes(),
        {
          path: collectorRoutePaths.LISTING_DETAIL,
          name: listingRouteName,
          component: () => {
            const { isLIBFlag } = getWhiteLabelState();
            return isLIBFlag.value
              ? import('@/views/artworks/artwork-detail-lib/ArtworkDetailLib.vue')
              : import('@/views/artworks/artwork-detail/ArtworkDetail.vue');
          },
          beforeEnter: [
            async (to, from, next) => {
              if (await isActivationLIB()) {
                await setWhiteLabelForLIB();
                next();
              } else {
                await checkActivationUnPublished(to, from, next);
              }
            },
          ],
        },

        {
          meta: { title: t('storefront.app.offer.detail.title') },
          path: collectorRoutePaths.OFFER_DETAIL,
          component: () => import('@/views/offers/offer-detail/OfferDetail.vue'),
          beforeEnter: [AuthGuard, checkActivationUnPublished],
        },
        {
          path: collectorRoutePaths.EDIT_OFFER,
          name: offerEditRouteName,
          component: () => import('@/views/offers/edit-offer/EditOffer.vue'),
          beforeEnter: [AuthGuard, checkActivationUnPublished],
        },
        {
          path: collectorRoutePaths.CREATE_OFFER,
          name: offerCreateRouteName,
          component: () => import('@/views/offers/create-offer/CreateOffer.vue'),
          beforeEnter: [
            checkActivationUnPublished,
            async (to, from, next) => {
              if (await isActivationLIB()) {
                await setWhiteLabelForLIB();
              }
              next();
            },
          ],
        },
        {
          path: collectorRoutePaths.INQUIRIES,
          redirect: { name: 'Offers' },
          component: () => import('@/views/inquiries/Inquiries.vue'),
          children: inquiryRoutes,
          beforeEnter: [AuthGuard, checkActivationUnPublished],
        },
        {
          meta: { title: t('storefront.app.account.title') },
          path: collectorRoutePaths.ACCOUNT,
          component: () => import('@/views/account/Account.vue'),
          beforeEnter: [AuthGuard, checkActivationUnPublished],
        },
        {
          meta: { title: t('storefront.app.profile.title') },
          path: collectorRoutePaths.PROFILE,
          component: () => import('@/views/profile/Profile.vue'),
          beforeEnter: [AuthGuard, checkActivationUnPublished],
        },
        {
          meta: { title: t('storefront.app.profile.edit.title') },
          path: collectorRoutePaths.EDIT_PROFILE,
          component: () => import('@/views/profile/edit-profile/EditProfile.vue'),
          beforeEnter: [AuthGuard, checkActivationUnPublished],
        },
        {
          meta: { title: t('storefront.app.about.title') },
          path: collectorRoutePaths.ABOUT_SECTION,
          component: () => import('@/views/about/About.vue'),
          beforeEnter: [checkActivationUnPublished],
        },
        {
          path: collectorRoutePaths.ABOUT,
          redirect: aboutMenu()[0].href || '/',
          beforeEnter: [checkActivationUnPublished],
        },
        {
          meta: { title: t('storefront.app.error.404.title') },
          path: '/unpublished',
          component: () => import('@/views/unPublishedPage/UnPublishedPage.vue'),
          beforeEnter: [checkActivationPublished],
        },
        {
          meta: { title: t('storefront.app.salesAgreement.detail.title') },
          path: collectorRoutePaths.SALES_AGREEMENT_DETAIL,
          component: () => import('@/views/sales-agreements/sales-agreement-details/SalesAgreementDetails.vue'),
          beforeEnter: [checkActivationUnPublished],
        },
        // TODO: Remove legacy route redirects Q3 2025
        {
          path: '/artworks',
          redirect: collectorRoutePaths.LISTINGS,
        },
        {
          path: '/artworks/:id',
          redirect: (to) => ({
            name: collectorRoutePaths.LISTING_DETAIL,
            params: { id: to.params.id },
          }),
        },
        {
          path: '/artworks/:id/offer/create',
          redirect: (to) => ({
            name: collectorRoutePaths.CREATE_OFFER,
            params: { id: to.params.id },
          }),
        },
        {
          path: '/:slug',
          name: viewingRoomRouteName,
          props: true,
          component: () => import('@/views/viewingrooms/Viewingroom.vue'),
          beforeEnter: [
            (to) => {
              const reserved = ['login', 'logout', 'reset-password', 'unverified', 'account', 'profile', 'about'];
              if (reserved.includes(String(to.params.slug))) {
                return { path: `/${to.params.slug}` };
              }
              return true;
            },
            checkViewingRoomExists,
            checkActivationUnPublished,
          ],
        },
      ],
    },
    {
      path: collectorRoutePaths.REGISTER_VERIFIED,
      component: () => import(`./auth/completed/Completed.vue`),
      meta: { context: 'emailVerified' },
      beforeEnter: [checkActivationUnPublished],
    },
    {
      path: collectorRoutePaths.PASSWORD_CHANGED,
      component: () => import(`./auth/completed/Completed.vue`),
      meta: { context: 'passwordChanged' },
      beforeEnter: [checkActivationUnPublished],
    },
    {
      meta: { title: t('storefront.app.register.title') },
      path: collectorRoutePaths.REGISTER_ACCOUNT,
      component: () => import('@/views/register/Register.vue'),
      beforeEnter: [checkActivationUnPublished],
    },
    {
      meta: { title: t('storefront.app.register.title') },
      path: collectorRoutePaths.REGISTER,
      redirect: collectorRoutePaths.REGISTER_ACCOUNT,
      beforeEnter: [checkActivationUnPublished],
    },
  ];
};
